import React, { useEffect, useState } from 'react';

const MatchStreamer = ({ matchId, streamUrl, fullscreenUrl, isVisible = false }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (isVisible && streamUrl) {
            setLoading(true);
            setError(false);

            // Simulate loading time for better UX
            const timer = setTimeout(() => {
                setLoading(false);
            }, 1000);

            return () => clearTimeout(timer);
        } else {
            setLoading(false);
            setError(false);
        }
    }, [isVisible, streamUrl]);

    if (!isVisible) {
        return null;
    }

    if (!streamUrl) {
        return (
            <div className="stream-container">
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-white">
                    <div className="mb-3">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                            <polyline points="22,17 12,21 2,17" stroke="currentColor" strokeWidth="2" fill="none" />
                            <polygon points="10,8 14,10 10,12" fill="currentColor" />
                        </svg>
                    </div>
                    <div className="fs-6 mb-2">No Stream Available</div>
                    <div className="fs-8 text-muted">Live stream is not available for this match</div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="stream-container">
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-white">
                    <div className="mb-3">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div className="fs-6 mb-2">Loading Stream...</div>
                    <div className="fs-8 text-muted">Please wait while we prepare the live stream</div>
                </div>
            </div>
        );
    }

    // Helper function to safely exit fullscreen
    const exitFullscreenSafely = () => {
        try {
            const isFullscreen = 
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement;

            if (isFullscreen) {
                if (document.exitFullscreen) {
                    document.exitFullscreen().catch(() => {});
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen().catch(() => {});
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen().catch(() => {});
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen().catch(() => {});
                }
            }
        } catch (err) {
            console.debug('Fullscreen exit error (ignored):', err);
        }
    };

    const handleFullscreenClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!fullscreenUrl) return;

        // Create a fullscreen overlay
        const overlay = document.createElement('div');
        overlay.id = 'fullscreen-stream-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 9999;
            background-color: #000;
            display: flex;
            flex-direction: column;
        `;
        
        // Create iframe
        const iframe = document.createElement('iframe');
        iframe.src = fullscreenUrl;
        iframe.style.cssText = 'flex: 1; width: 100%; height: 100%; border: none; background-color: #000;';
        iframe.allowFullScreen = true;
        iframe.title = 'Fullscreen Stream';
        
        // Cleanup function
        const cleanup = () => {
            if (document.body.contains(overlay)) {
                document.body.removeChild(overlay);
            }
        };
        
        // Function to remove all event listeners
        const removeAllListeners = () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
            window.removeEventListener('keydown', handleEscape);
        };
        
        // Handle escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape' && document.body.contains(overlay)) {
                exitFullscreenSafely();
                cleanup();
                removeAllListeners();
            }
        };
        
        // Listen for fullscreen changes (when user exits fullscreen via browser controls)
        const handleFullscreenChange = () => {
            const isFullscreen = 
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement;
            
            if (!isFullscreen && document.body.contains(overlay)) {
                cleanup();
                removeAllListeners();
            }
        };
        
        // Create close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'btn btn-sm btn-secondary position-absolute';
        closeBtn.style.cssText = 'top: 10px; right: 10px; z-index: 10000; color: #fff; padding: 10px; min-width: 44px; min-height: 44px;';
        closeBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" />
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" />
            </svg>
        `;
        closeBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            exitFullscreenSafely();
            cleanup();
            removeAllListeners();
        };
        
        overlay.appendChild(closeBtn);
        overlay.appendChild(iframe);
        document.body.appendChild(overlay);
        
        // Add event listeners
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        window.addEventListener('keydown', handleEscape);
        
        // Request fullscreen
        setTimeout(() => {
            if (overlay.requestFullscreen) {
                overlay.requestFullscreen().catch(() => {
                    // Fullscreen failed, but overlay is still visible
                });
            } else if (overlay.webkitRequestFullscreen) {
                overlay.webkitRequestFullscreen();
            } else if (overlay.mozRequestFullScreen) {
                overlay.mozRequestFullScreen();
            } else if (overlay.msRequestFullscreen) {
                overlay.msRequestFullscreen();
            }
        }, 100);
    };

    return (
        <div className="match-streamer-container">
            <style>
                {`
          .match-streamer-container {
            max-width: 620px;
            width: 100%;
            margin: 0 auto;
          }
          .stream-container {
            width: 100%;
            height: 16rem;
            border: rgba(0,0,0,0.12) solid 1px;
            position: relative;
            background-color: #000;
          }
          .stream-iframe {
            width: 100%;
            height: 100%;
            border: none;
            pointer-events: auto;
          }
          .fullscreen-button {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 1000;
            background-color: rgba(0, 0, 0, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 6px;
            padding: 12px;
            min-width: 44px;
            min-height: 44px;
            cursor: pointer;
            pointer-events: auto;
            touch-action: manipulation;
            -webkit-tap-highlight-color: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.2s;
          }
          .fullscreen-button:active {
            background-color: rgba(0, 0, 0, 0.95);
            transform: scale(0.95);
          }
          .fullscreen-button:hover {
            background-color: rgba(0, 0, 0, 0.9);
          }
          @media (hover: none) {
            .fullscreen-button:hover {
              background-color: rgba(0, 0, 0, 0.8);
            }
          }
        `}
            </style>

            <div className="stream-container">
                <iframe
                    src={streamUrl}
                    className="stream-iframe"
                    title="Match Stream"
                    allowFullScreen
                    onError={() => setError(true)}
                    onLoad={() => setError(false)}
                />
                {fullscreenUrl && !error && !loading && (
                    <button
                        className="fullscreen-button"
                        onClick={handleFullscreenClick}
                        onTouchStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFullscreenClick(e);
                        }}
                        title="Enter Fullscreen"
                        aria-label="Enter Fullscreen"
                        type="button">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ color: '#fff', pointerEvents: 'none' }}>
                            <path
                                d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                            />
                        </svg>
                    </button>
                )}
                {error && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white bg-dark bg-opacity-75">
                        <div className="mb-3">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" />
                                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
                            </svg>
                        </div>
                        <div className="fs-6 mb-2">Stream Error</div>
                        <div className="fs-8 text-muted">Unable to load the live stream</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MatchStreamer;
